import type { SupabaseClient } from '@supabase/supabase-js'
import type {
  Entitlements,
  PullCursor,
  PullResponse,
  PushResult,
  PushTx,
  RegisteredCounter,
  SyncTransport,
} from '@ims/data'
import { toSyncError, unwrap } from './errors.ts'

const FETCH_CHUNK = 100

/** SyncTransport over supabase-js: the sync RPCs plus Realtime notifications. */
export class SupabaseTransport implements SyncTransport {
  constructor(private readonly client: SupabaseClient) {}

  async push(tenantId: string, installationId: string, txs: PushTx[]): Promise<PushResult[]> {
    const data = unwrap(
      await this.client.rpc('sync_push', {
        p_tenant_id: tenantId,
        p_installation_id: installationId,
        p_txs: txs,
      }),
    ) as { results: PushResult[] }
    return data.results
  }

  async pull(tenantId: string, cursor: PullCursor | null, limit: number): Promise<PullResponse> {
    return unwrap(
      await this.client.rpc('sync_pull', {
        p_tenant_id: tenantId,
        p_cursor: cursor,
        p_limit: limit,
      }),
    ) as PullResponse
  }

  async fetchRows(table: string, ids: string[]): Promise<Record<string, unknown>[]> {
    const rows: Record<string, unknown>[] = []
    for (let i = 0; i < ids.length; i += FETCH_CHUNK) {
      const chunk = ids.slice(i, i + FETCH_CHUNK)
      rows.push(
        ...(unwrap(await this.client.from(table).select('*').in('id', chunk)) as Record<
          string,
          unknown
        >[]),
      )
    }
    return rows
  }

  async entitlements(tenantId: string, installationId: string): Promise<Entitlements> {
    return unwrap(
      await this.client.rpc('device_checkin', {
        p_tenant_id: tenantId,
        p_installation_id: installationId,
      }),
    ) as Entitlements
  }

  async registerCounter(
    tenantId: string,
    installationId: string,
    name?: string,
  ): Promise<RegisteredCounter> {
    return unwrap(
      await this.client.rpc('register_counter', {
        p_tenant_id: tenantId,
        p_installation_id: installationId,
        p_name: name ?? null,
      }),
    ) as RegisteredCounter
  }

  subscribe(tenantId: string, onChange: () => void): () => void {
    const channel = this.client.channel(`tenant:${tenantId}`, { config: { private: true } })
    let closed = false
    void this.client.realtime
      .setAuth()
      .then(() => {
        if (!closed) channel.on('broadcast', { event: 'changed' }, () => onChange()).subscribe()
      })
      .catch((error: unknown) =>
        console.warn('realtime subscribe failed', toSyncError(error).message),
      )
    return () => {
      closed = true
      void this.client.removeChannel(channel)
    }
  }
}
